// src/hooks/useToast.js
import { useState, useCallback } from 'react';

export const useToast = () => {
  const [toastState, setToastState] = useState({
    open: false,
    message: '',
    severity: 'info',
    title: '',
    autoHideDuration: 6000,
    position: { vertical: 'bottom', horizontal: 'right' },
    variant: 'filled',
  });

  const showToast = useCallback((config) => {
    // If config is a string, treat it as message with default error severity
    if (typeof config === 'string') {
      setToastState(prev => ({
        ...prev,
        open: true,
        message: config,
        severity: 'error',
        title: '',
      }));
      return;
    }

    // If config is an object, merge with default values
    setToastState(prev => ({
      ...prev,
      open: true,
      message: config.message || '',
      severity: config.severity || 'info',
      title: config.title || '',
      autoHideDuration: config.autoHideDuration || 6000,
      position: config.position || { vertical: 'bottom', horizontal: 'right' },
      variant: config.variant || 'filled',
    }));
  }, []);

  const hideToast = useCallback(() => {
    setToastState(prev => ({
      ...prev,
      open: false,
    }));
  }, []);

  // Convenience methods for different severities
  const showSuccess = useCallback((message, title) => {
    showToast({
      message,
      severity: 'success',
      title,
    });
  }, [showToast]);

  const showError = useCallback((message, title) => {
    showToast({
      message,
      severity: 'error',
      title,
    });
  }, [showToast]);

  const showWarning = useCallback((message, title) => {
    showToast({
      message,
      severity: 'warning',
      title,
    });
  }, [showToast]);

  const showInfo = useCallback((message, title) => {
    showToast({
      message,
      severity: 'info',
      title,
    });
  }, [showToast]);

  return {
    toastState,
    showToast,
    hideToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};